import { Injector } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { FormlyFieldConfig } from "@ngx-formly/core";
import { LookupGetService } from "../../../../service/lookup-service/lookup-get.service";
import { FieldDefinition } from "../../../../../sections/createSendProductReport/message/fieldDefinition";
import { AuthorisedProduct } from "../../../Data/Art57Data/AuthorisedProduct";
import { Art57Node } from "../Art57Node";
import { Art57NodeUtils } from "../Art57NodeUtils";
import { TreeNodeAuthorisedPharmaceuticalFormList } from "../ListEntity/TreeNodeAuthorisedPharmaceuticalFormList";
import { TreeNodeDrugsATCList } from "../ListEntity/TreeNodeDrugsATCList";
import { TreeNodeDrugsIndicationsList } from "../ListEntity/TreeNodeDrugsIndicationsList";
import { TreeNodeMedicinalProductTypeList } from "../ListEntity/TreeNodeMedicinalProductTypeList";
import { TreeNodePharmaceuticalProductsList } from "../ListEntity/TreeNodePharmaceuticalProductsList";
import { TreeNodeProductAttachmentList } from "../ListEntity/TreeNodeProductAttachmentList";
import { TreeNodePreviousEVCodeList } from "../ListEntity/TreeNodePreviousEVCodeList";
import { Art57Section } from "../../../Art57Section/Art57Section";
import { ErrorControlUtil } from "../../../ErrorControlUtil";
import { environment } from "../../../../../../environments/environment";
import { ErrorType } from "../../ErrorType";
import { AuthService } from "../../../../service/auth-service/auth-service.service";

export class TreeNodeAuthorisedProduct extends Art57Node {
  constructor(injector: Injector, parent: Art57Node) {
    super(
      injector,
      new AuthorisedProduct(
        new FieldDefinition(injector),
        new FormGroup({}),
        {}
      ),
      '{param1} - Authorised {param2} '
    );

    this.parent = parent;
    this.labelNodeName = 'Authorised Product';
    this.data.model.operationtype = 1; // insert
    // localnumber is calculated in createchiltemplate of its parent because requires nodetype
    this.ioc!.get(Art57Section).generateNumColumValue(this);
    this.refreshLabel();
  }

  override refreshLabel(lazy:boolean=true): void {
    if (
      this.data &&
      this.data.model &&
      this.data.model.presentationname &&
      this.data.model.presentationname.productname
    ) {
      this.label =
        this.labelTemplate +
        ' - ' +
        this.data.model.presentationname.productname;
    } else {
      this.label = this.labelTemplate;
    }
    const param2 = this.data.model && this.data.model.operationtype && this.data.model.operationtype === 1?'':'- ' + this.data.model.ev_Code;
    this.label = this.label.replace('{param1}', this.ioc!.get(LookupGetService).retrieveValueFromCache('operationtype', this.data.model.operationtype)).replace('{param2}',param2);
  }

  override getMessage(): any {
    //Authorised product level
    let authorisedproduct: any = {};

    if (this.data.model) {
      authorisedproduct = { ...this.data.model };
    }

    if (authorisedproduct.authorisation && authorisedproduct.authorisation.authorisationstatus && !authorisedproduct.authorisation.authorisationstatus.value) {
      delete authorisedproduct.authorisation.authorisationstatus;
    }

    if (authorisedproduct.authorisation && authorisedproduct.authorisation.authorisationcountrycode && !authorisedproduct.authorisation.authorisationcountrycode.value) {
      delete authorisedproduct.authorisation.authorisationcountrycode;
    }
    
    if (authorisedproduct.authorisation && authorisedproduct.authorisation.intensivemonitoring && !authorisedproduct.authorisation.intensivemonitoring.value) {
      delete authorisedproduct.authorisation.intensivemonitoring;
    }

    //Authorised product children
    let childrenModel: any = {};
    this.children?.forEach((node) => {
      let authorisedProductChild = node as Art57Node;
      let art57Message = authorisedProductChild.getMessage();
      if (art57Message != null) {
        if (
          !(authorisedProductChild instanceof TreeNodeMedicinalProductTypeList)
        ) {
          childrenModel = { ...childrenModel, ...art57Message };
        } else {
          childrenModel = {
            ...childrenModel,
            authorisation: {
              ...authorisedproduct.authorisation,
              ...art57Message,
            },
          };
        }
      }
    });

    if (childrenModel) {
      authorisedproduct = { ...authorisedproduct, ...childrenModel };
    }

    return authorisedproduct;
  }



  override setMessage(jsonObject: any): void {
    let sectionToLoad = '';
    if (jsonObject.operationtype === 1) {
      if(this.ioc?.get(AuthService).isDatamaskedUser()) {
        delete jsonObject.qppv;
        delete jsonObject.qppvcode;
        delete jsonObject.mfl;
        delete jsonObject.mflcode;
      }
      if ( // If it is a non Valid Authorisation Status, set it to Valid by default
          ((jsonObject.authorisation) && 
           (jsonObject.authorisation.authorisationstatus) && 
           (this.ioc?.get(LookupGetService).retrieveValueFromCache('validauthorisationstatus', jsonObject.authorisation.authorisationstatus.value) == '')
          ) ||
          // NOTE: this shouldn't happen - if it is a Valid Authorisation Status but the object is empty, set it to Valid by default
          (
           (jsonObject.authorisation) && 
           (typeof jsonObject.authorisation.authorisationstatus === 'object') &&
           (Object.keys(jsonObject.authorisation.authorisationstatus).length === 0)
          )
         ) {
        jsonObject.authorisation.authorisationstatus = {};
        jsonObject.authorisation.authorisationstatus.value = 1;
        jsonObject.authorisation.authorisationstatus.name = 'Valid';
        jsonObject.authorisation.withdrawndate = null;
        jsonObject.authorisation.withdrawndateformat = null;
      }
    }
    super.setMessage(jsonObject);

    sectionToLoad = 'authorisation';
    Art57NodeUtils.generateFormlyModel(this.data.fields.find((item: FormlyFieldConfig) => item.key === sectionToLoad).fieldGroup, jsonObject, this.data.model, sectionToLoad);

    sectionToLoad = 'presentationname';
    Art57NodeUtils.generateFormlyModel(this.data.fields.find((item: FormlyFieldConfig) => item.key === sectionToLoad).fieldGroup, jsonObject, this.data.model, sectionToLoad);

    // Import Children Nodes
    if (jsonObject.authorisation && jsonObject.authorisation.medicinalproducttypes) {
      Art57NodeUtils.importNodeType(this, jsonObject.authorisation.medicinalproducttypes, 'medicinalproducttype', TreeNodeMedicinalProductTypeList);
    }

    if (jsonObject.authpharmforms) {
      Art57NodeUtils.importNodeType(this, jsonObject.authpharmforms, 'authpharmform', TreeNodeAuthorisedPharmaceuticalFormList);
    }

    if (jsonObject.pharmaceuticalproducts) {
      Art57NodeUtils.importNodeType(this, jsonObject.pharmaceuticalproducts, 'pharmaceuticalproduct', TreeNodePharmaceuticalProductsList);
    }

    if (jsonObject.productatcs) {
      Art57NodeUtils.importNodeType(this, jsonObject.productatcs, 'productatc', TreeNodeDrugsATCList);
    }

    if (jsonObject.productindications) {
      Art57NodeUtils.importNodeType(this, jsonObject.productindications, 'productindication', TreeNodeDrugsIndicationsList);
    }

    /* TODO: FIX because in XML imported file to JSON there is no way to know if it Authorised or Development Node type
    */
    if (jsonObject.previousevcodes) {
      Art57NodeUtils.importNodeType(this, jsonObject.previousevcodes, 'previousevcode', TreeNodePreviousEVCodeList, { nodeTypeToCreateName: '', nodeType: undefined });
    }

    if (jsonObject.ppiattachments) {
      Art57NodeUtils.importNodeType(this, jsonObject.ppiattachments, 'ppiattachment', TreeNodeProductAttachmentList, { nodeTypeToCreateName: '', nodeType: 1 });
    }

    this.refreshLabel(true);

  }

  override getListItem(): any[] {
    return [
      this.data.model && this.data.model.ev_Code
        ? this.data.model.ev_Code
        : '-',
      this.data.model &&
      this.ioc!.get(LookupGetService).retrieveValueFromCache(
        'operationtype',
        this.data.model.operationtype
      ),
      'Authorised',
      this.data.model && this.data.model.presentationname
        ? this.data.model.presentationname.productname
        : '',
    ];
  }

  override cloneNode(newParent: Art57Node): Art57Node|undefined {
    // don't allow clonning for Insert AMP if Datamasked user
    if (this.ioc!.get(AuthService).isDatamaskedUser() && this.data.model.operationtype == 1) {
      this.ioc!.get(Art57Section).showWarningTooltipMessage('Insertion of Authorised Medicinal Products not allowed, operation removed');
      return undefined; 
    }

    let clonedNode: TreeNodeAuthorisedProduct = new TreeNodeAuthorisedProduct(
      this.ioc!,
      newParent
    );

      const currentLocalNumber = this.ioc!.get(LookupGetService).getNodeLocalNumber(clonedNode, this);
      clonedNode.data.model = JSON.parse(JSON.stringify(this.data.model));
      clonedNode.data.model.localnumber = currentLocalNumber;
      this.ioc!.get(LookupGetService).fixLocalNumber(clonedNode.data.model.localnumber);
    
    clonedNode.refreshLabel();  
    clonedNode.expanded = this.expanded && environment.userPreferences.autoexpandNodesOnCloning ||environment.userPreferences.alwaysAutoexpandNodes
 

    this.children?.forEach((child) => {
      let childToClone = child as Art57Node;
      let clonedChildren = childToClone.cloneNode(clonedNode);
      if (clonedChildren != undefined) {
        clonedNode.children?.push(clonedChildren);
      }
    });

    return clonedNode;
  }

  override validateBusinessRules(): any[] {
      const sameReferenceBychildBusinessRulesErrors = ErrorControlUtil.sameReferenceByChild(this, "presentationname", "productname");

      const sameModelValuesBusinessErrors = ErrorControlUtil.sameModelValues(this);
      const authorisation = this.data.model.authorisation;
      let businessRulesErrors = [];
      
     // if (sameReferenceBychildBusinessRulesErrors.length>0) {
        businessRulesErrors.push(...sameReferenceBychildBusinessRulesErrors);
     // }

      if (sameModelValuesBusinessErrors.length>0){
        businessRulesErrors.push(...sameModelValuesBusinessErrors);
      }
      
    return businessRulesErrors;
  }
}